import { useState, useRef } from "react";
import { TbTrash } from "react-icons/tb";
import { uploadToAws } from "helpers/uploader";
import { displayError } from "utils";
import { UploadedFile, FundingForm } from "../types";
import { useFormik, FormikErrors } from "formik";
import documentIcon from "assets/icons/document.svg";
import documentTextIcon from "assets/icons/document-text.svg";

interface UploadFileProps {
  values: FundingForm;
  setFieldValue: (
    field: keyof FundingForm,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<FundingForm>>;
  onFieldChange?: (field: keyof FundingForm, value: any) => void;
}

const UploadFile = ({
  values,
  setFieldValue,
  onFieldChange,
}: UploadFileProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;

    const file = files[0];

    try {
      const uploadedFileKey = await uploadToAws({
        value: file,
        handleProgress: (val) => setProgress(Number(val)),
      });
      if (!uploadedFileKey) {
        displayError("File upload failed");
        return;
      }

      const uploadedFile: UploadedFile = {
        S3Key: uploadedFileKey,
        Bucket: "bnkle-iam-docs",
        type: file.type,
        name: file.name,
      };

      const newFiles = [...(values.uploadedFiles || []), uploadedFile];
      setFieldValue("uploadedFiles", newFiles, false);
      if (onFieldChange) {
        onFieldChange("uploadedFiles", newFiles);
      }
      setProgress(0);
    } catch (err) {
      console.error("Upload failed:", err);
      setProgress(0);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleDelete = (S3Key: string) => {
    const newFiles = (values.uploadedFiles || []).filter(
      (file) => file.S3Key !== S3Key
    );
    setFieldValue("uploadedFiles", newFiles, false);
    if (onFieldChange) {
      onFieldChange("uploadedFiles", newFiles);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  return (
    <div
      className='flex flex-col gap-2 text-blueShades-4'
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
    >
      <label className='font-SatoshiMedium text-sm '>
        Shortlist of Previous Project Completed (Company Profile)
      </label>
      <div className='flex flex-col justify-center items-center gap-2 border-2 border-blueShades-2 min-h-[118px] w-full rounded-lg'>
        <img src={documentIcon} alt='' className='w-6 h-6' />
        <span className='text-xs font-medium'>
          Drag & Drop or Select file button to upload
        </span>
        <input
          id='file-input'
          type='file'
          onChange={handleChange}
          style={{ display: "none" }}
          ref={fileInputRef}
        />
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          style={{
            backgroundColor: "transparent",
            borderColor: "#D1D5DB",
            color: "#023061",
            padding: "10px 18px",
          }}
        >
          Upload Files
        </button>
      </div>
      {progress > 0 && (
        <div className='mt-2'>
          <div className='h-2 w-full bg-ashShade-0 rounded-[4px] overflow-hidden'>
            <div
              className={`h-full bg-blueShades-4 w-[${progress}%] transition-[width] duration-300 ease-in-out`}
            />
          </div>
        </div>
      )}
      {values.uploadedFiles?.length > 0 && (
        <div className='flex flex-col gap-2'>
          {values.uploadedFiles.map((file) => (
            <div
              key={file.S3Key}
              className='flex gap-4 items-center border border-[#E5EAF0] bg-ashShade-17 p-3 rounded-lg'
            >
              <div className='flex items-center gap-4 w-[90%]'>
                <img src={documentTextIcon} alt='' className='w-6 h-6' />
                <span className='text-sm font-medium text-blueShades-4'>
                  {file.name}
                </span>
              </div>
              <button onClick={() => handleDelete(file.S3Key)}>
                <TbTrash className='text-[#FF6B6B]' />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadFile;
